import * as React from "react";
import { connect } from "react-redux";

import { PAYMENT_RESULT_TOAST_ID } from "@config/consts";

import { PAYMENT_STATUS } from "@typings/payments";

import { PaymentProcessed as PaymentProcessedOrganism } from "@ui/Organisms";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";

import { showToast, TypeOptions } from "@containers/Toastify";

import { useTranslate } from "@hooks/useTranslate";
import { useTelemetry } from "@hooks/useTelemetry";
import { useAuth } from "@hooks/useAuth";
import { useServerSideGTM } from "@hooks/useServerSideGTM";
import { useGTM, BookingFunnel } from "@hooks/useGTM";

import {
  fetchPaymentStatus,
  setReferenceNumber,
  setOriginRoute,
  setTargetRoute,
} from "@app/redux/payment/payment.actions";
import {
  getTargetRoute,
  getPaymentStatus,
  getPublicMessage,
  getOriginRoute,
} from "@app/redux/payment/payment.selectors";
import { IStore } from "@app/redux/reducers";

interface Props {
  paymentStatus: PAYMENT_STATUS;
  publicMessage: string | null;
  originRoute: string;
  targetRoute: string;
  fetchPaymentStatus: () => Promise<AsyncActionResult>;
  setReferenceNumber: (referenceNumber: string) => void;
  setOriginRoute: (originRoute: string, serviceLocationId: string) => void;
  setTargetRoute: (targetRoute: string) => void;
}

export const PaymentProcessedComponent = (props: Props): React.ReactElement => {
  const translate = useTranslate();
  const { sendEvent } = useTelemetry();
  const { isUserDataFetched } = useAuth();
  const { sendFinishedBooking } = useServerSideGTM();
  const gtm = useGTM();

  const searchParams = new URLSearchParams(window.location.href);

  const showNotification = (
    message: string | null,
    type: TypeOptions,
  ): void => {
    showToast(translate(message || "batmaid_pwa_generic.payment_timeout"), {
      type,
      toastId: PAYMENT_RESULT_TOAST_ID,
    });
  };

  React.useEffect(() => {
    if (
      ![PAYMENT_STATUS.AUTHORIZED, PAYMENT_STATUS.PAID].includes(
        props.paymentStatus,
      )
    ) {
      return;
    }

    const reservationUuid = searchParams.get("reservationUuid");
    const contractUuid = searchParams.get("contractUuid");
    const pageUrl = window.location.href;

    if (contractUuid) {
      // GTM Trigger - "Booking Funnel - Home Cleaning - Purchase"
      gtm.push(
        {
          funnelStep: BookingFunnel.FUNNEL_STEP.PURCHASE,
          contractId: contractUuid,
          paymentStatus: props.paymentStatus,
        },
        "booking-funnel-view",
      );
    }

    if (reservationUuid) {
      sendFinishedBooking(reservationUuid, {
        pageUrl,
      });
    } else {
      sendEvent("server-side-GTM", {
        context: {
          step: "payment-processed",
          method: "sendFinishedBooking",
          issue: "no reservationUuid",
          reservationUuid,
          pageUrl,
        },
      });
    }
  }, [props.paymentStatus]);

  return (
    <React.Fragment>
      {isUserDataFetched && (
        <PaymentProcessedOrganism
          paymentStatus={props.paymentStatus}
          publicMessage={props.publicMessage}
          originRoute={props.originRoute}
          targetRoute={props.targetRoute}
          fetchPaymentStatus={props.fetchPaymentStatus}
          setReferenceNumber={props.setReferenceNumber}
          setOriginRoute={props.setOriginRoute}
          setTargetRoute={props.setTargetRoute}
          showMessageToast={showNotification}
          sendEvent={sendEvent}
          htmlMetaTitleText={`${translate(
            "batmaid_pwa_generic.payment_is_being_processed",
          )}`}
          htmlMetaDescriptionText={translate(
            "batmaid_pwa_generic.payment_is_being_processed",
          )}
        />
      )}
    </React.Fragment>
  );
};

const mapState = (store: IStore) => ({
  paymentStatus: getPaymentStatus(store),
  publicMessage: getPublicMessage(store),
  originRoute: getOriginRoute(store),
  targetRoute: getTargetRoute(store),
});

export const PaymentProcessed = connect(mapState, {
  fetchPaymentStatus,
  setReferenceNumber,
  setOriginRoute,
  setTargetRoute,
})(PaymentProcessedComponent);
